import { useEffect, useMemo, useState } from "react";
import type { ShareItem } from "../../mocks/animals";
import { useAnimals } from "../../hooks/useAnimals";
import { useCustomers } from "../../hooks/useCustomers";
import { usePayments } from "../../hooks/usePayments";
import BackdropCloseContainer from "../ui/BackdropCloseContainer";
import {
  analyzeShareTransfer,
  getAvailableSlotNos,
  transferShareSale,
} from "../../lib/shareTransfer";
import { getDisplayKupeNo, getDisplaySiparisNo } from "../../lib/animalLabels";
import { getDeliveryAssignments, setAllDeliveryAssignments } from "../../lib/deliveryStore";

interface Props {
  share: ShareItem;
  sourceAnimalId: number;
  onClose: () => void;
}

function sortNumbers(values: number[]) {
  return [...values].sort((a, b) => a - b);
}

export default function ShareTransferModal({ share, sourceAnimalId, onClose }: Props) {
  const { animals, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();

  const [targetAnimalId, setTargetAnimalId] = useState<number>(0);
  const [selectedSlotNos, setSelectedSlotNos] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const analysis = useMemo(
    () => analyzeShareTransfer({ shareId: share.id, animals }),
    [animals, share.id]
  );

  const sourceAnimal = useMemo(
    () => analysis.sourceAnimal ?? animals.find((animal) => animal.id === sourceAnimalId) ?? null,
    [analysis.sourceAnimal, animals, sourceAnimalId]
  );

  const targetAnimal = useMemo(
    () => analysis.targetCandidates.find((animal) => animal.id === targetAnimalId) ?? null,
    [analysis.targetCandidates, targetAnimalId]
  );

  const availableSlotNos = useMemo(
    () => (targetAnimal ? getAvailableSlotNos(targetAnimal) : []),
    [targetAnimal]
  );

  useEffect(() => {
    if (analysis.targetCandidates.length === 1) {
      setTargetAnimalId(analysis.targetCandidates[0].id);
    }
  }, [analysis.targetCandidates]);

  useEffect(() => {
    setSelectedSlotNos([]);
  }, [targetAnimalId]);

  function toggleSlot(slotNo: number) {
    setSelectedSlotNos((prev) => {
      if (prev.includes(slotNo)) {
        return prev.filter((item) => item !== slotNo);
      }

      if (prev.length >= Math.max(1, share.hisseSayisi || 1)) {
        return prev;
      }

      return sortNumbers([...prev, slotNo]);
    });
  }

  async function handleTransfer() {
    if (!targetAnimal) {
      window.alert("Lütfen aktarılacak hayvanı seçin.");
      return;
    }

    if (selectedSlotNos.length !== Math.max(1, share.hisseSayisi || 1)) {
      window.alert(`Lütfen ${Math.max(1, share.hisseSayisi || 1)} adet slot seçin.`);
      return;
    }

    const ok = window.confirm(
      [
        `${share.hisseNo} satışı aktarılacak.`,
        `Yeni Küpe No: ${getDisplayKupeNo(targetAnimal)}`,
        `Yeni Sipariş No: ${getDisplaySiparisNo(targetAnimal)}`,
        `Yeni Slot: ${selectedSlotNos.join(", ")}`,
        "",
        "Bağlı ödeme ve taşıma kayıtları yeni hayvana taşınacak.",
      ].join("\n")
    );

    if (!ok) return;

    setSaving(true);

    try {
      const result = transferShareSale({
        shareId: share.id,
        targetAnimalId: targetAnimal.id,
        targetSlotNos: selectedSlotNos,
        animals,
        customers,
        payments,
        deliveries: getDeliveryAssignments(),
      });

      if (!result.movedShare) {
        window.alert("Satış aktarılamadı. Slotlar dolu olabilir.");
        return;
      }

      await setAllAnimals(() => result.animals);
      await setAllCustomers(() => result.customers);
      await setAllPayments(() => result.payments);
      await setAllDeliveryAssignments(() => result.deliveries);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <BackdropCloseContainer
      onClose={onClose}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/35 backdrop-blur-sm px-0 sm:px-4"
    >
      <div className="w-full sm:max-w-xl bg-farm-warm border border-farm-border rounded-t-farm sm:rounded-farm shadow-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-cream">
          <div>
            <p className="font-heading font-semibold text-sm text-farm-brown">
              Satışı Farklı Hayvana Aktar
            </p>
            <p className="font-body text-[11px] text-farm-muted mt-0.5">
              {share.musteriAdi} · {share.hisseNo}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-farm-muted hover:text-farm-brown hover:bg-white/70 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2">
              <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Mevcut Küpe
              </p>
              <p className="font-body text-sm font-semibold text-farm-brown">
                {getDisplayKupeNo(sourceAnimal ?? { siraNo: 0 })}
              </p>
            </div>
            <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2">
              <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Mevcut Slot
              </p>
              <p className="font-body text-sm font-semibold text-farm-brown">
                {(share.slotNos ?? []).length > 0 ? (share.slotNos ?? []).join(", ") : "—"}
              </p>
            </div>
            <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2">
              <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                Taşınacak Hisse
              </p>
              <p className="font-body text-sm font-semibold text-farm-brown">
                {Math.max(1, share.hisseSayisi || 1)} adet
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="font-body text-xs font-semibold text-farm-brown">
              Yeni Hayvan
            </label>
            <select
              value={targetAnimalId || ""}
              onChange={(e) => setTargetAnimalId(Number(e.target.value) || 0)}
              className="w-full px-3 py-2.5 rounded-lg border border-farm-border bg-farm-cream text-sm text-farm-brown focus:outline-none focus:border-farm-earth"
            >
              <option value="">Hayvan seçin</option>
              {analysis.targetCandidates.map((animal) => {
                const availableCount = getAvailableSlotNos(animal).length;
                return (
                  <option key={animal.id} value={animal.id}>
                    Küpe {getDisplayKupeNo(animal)} · Sipariş {getDisplaySiparisNo(animal)} · {availableCount} boş slot
                  </option>
                );
              })}
            </select>
            {analysis.targetCandidates.length === 0 && (
              <p className="font-body text-xs text-red-600">
                Bu satışı taşıyacak kadar boş slotu olan başka hayvan bulunamadı.
              </p>
            )}
          </div>

          {targetAnimal && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="font-body text-xs font-semibold text-farm-brown">
                  Yeni Slot Seçimi
                </label>
                <p className="font-body text-[11px] text-farm-muted">
                  {Math.max(1, share.hisseSayisi || 1)} slot seç
                </p>
              </div>

              <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-3">
                <div className="flex flex-wrap gap-2">
                  {availableSlotNos.map((slotNo) => {
                    const selected = selectedSlotNos.includes(slotNo);
                    return (
                      <button
                        key={slotNo}
                        type="button"
                        onClick={() => toggleSlot(slotNo)}
                        className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors cursor-pointer ${
                          selected
                            ? "bg-farm-earth text-white border-farm-earth"
                            : "bg-white text-farm-brown border-farm-border hover:border-farm-earth/40"
                        }`}
                      >
                        Slot {slotNo}
                      </button>
                    );
                  })}
                </div>
              </div>

              <p className="font-body text-xs text-farm-muted">
                Seçilen yeni slot: {selectedSlotNos.length > 0 ? selectedSlotNos.join(", ") : "—"}
              </p>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-farm-border bg-farm-cream flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-full border border-farm-border text-farm-muted font-body text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleTransfer}
            disabled={saving || !targetAnimal || selectedSlotNos.length !== Math.max(1, share.hisseSayisi || 1)}
            className="flex-1 py-2 rounded-full bg-farm-earth text-white font-body text-xs font-semibold hover:bg-farm-brown transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Aktarılıyor..." : "Satışı Aktar"}
          </button>
        </div>
      </div>
    </BackdropCloseContainer>
  );
}
